import type { ArticleItem, FolderItem, ViewItem } from '@easterngraphics/wcf/modules/bsk';
import type { ArticleElement, ArticleManager, MainArticleElement } from '@easterngraphics/wcf/modules/cf';
import type App from '@egr/xbox/base-app/App';
import type { ShareFormat } from '@egr/xbox/base-app/Share';
import type { BasketManager } from '@egr/xbox/basket-view/managers/BasketManager';
import type { ShareContent, ShareOptions } from '@egr/xbox/share/Types';
import type { AsyncFunction } from '@egr/xbox/utils/Types';

import { TaskCanceledError } from '../utils/Promise';

import { updateArticle, updateBasket } from './ShareManagerUtils';
import { ShareTask } from './ShareTask';
import { shareObxHandler } from './handler/ObxHandler';

import { getAllBasketIds } from '@egr/xbox/egr-gui-utils/items/getAllBasketIds';

type AppRequirement = App & { basketManager: BasketManager };

/**
 * Note: the callback should return an empty array if the content could not be created
 */
type ShareContentCallback = (task: ShareTask, formats: ReadonlyArray<string>) => AsyncIterableIterator<ShareContent>;

export interface ShareContentGenerator {
    formats: ReadonlyArray<string> | undefined;
    callback: ShareContentCallback;
}

interface ShareContentOptions {
    requiresBasketUpdate?: boolean;
    requiresArticleUpdate?: boolean;
}

export const ShareManager = new class ShareManager {
    private callbacks: Array<ShareContentGenerator> = [];

    protected requiresArticleUpdate: Set<string> = new Set<string>();
    protected requiresBasketUpdate: Set<string> = new Set<string>();

    public set updateArticle(callback: AsyncFunction<boolean>) {
        this._updateArticle = callback;
    }

    public set updateBasket(callback: AsyncFunction<boolean>) {
        this._updateBasket = callback;
    }

    public constructor() {
        this.getItemsForShare = this.getItemsForShare.bind(this);
        // register some simple default handlers
        this.registerShareContent(['OBX'], shareObxHandler, { requiresBasketUpdate: true });
    }

    public get formats(): Array<string> {
        return this.callbacks.reduce(
            (value: Array<string>, current: ShareContentGenerator) => {
                if (current.formats?.length) {
                    return [...value, ...current.formats];
                }
                return value;
            },
            []
        );
    }

    /**
     * This function is used to register provider functions used for sharing content.
     *
     * @param formats a list of sharing keys handled by the callback; use `undefined` to get all selected formats
     * @param callback the content provide
     */
    public registerShareContent<CustomFormat extends string = ShareFormat>(
        formats: Array<ShareFormat | CustomFormat>,
        callback: ShareContentCallback,
        options: ShareContentOptions = {},
    ): void {
        this.updateRequirements(formats, options);

        this.callbacks.push({
            formats,
            callback
        });
    }

    public registerCustomShareContent(
        format: Exclude<string, ShareFormat>,
        callback: ShareContentCallback,
        options: ShareContentOptions = {},
    ) {
        this.updateRequirements([format], options);

        this.callbacks.push({
            formats: [format],
            callback
        });
    }

    public async createContent(
        items: ReadonlyArray<ArticleElement | ViewItem | ArticleItem | FolderItem>,
        formats: ReadonlyArray<string>,
        options: Omit<ShareOptions, 'items' |'itemIds' | 'formats'>
    ): Promise<ShareTask | null> {
        const itemIds = Array.from(getAllBasketIds(items));

        return this.getShareTask({
            ...options,
            formats: new Set(formats),
            items,
            itemIds
        });
    }

    public async getItemsForShare(
        formats: ReadonlyArray<string>,
        app: AppRequirement
    ): Promise<ReadonlyArray<MainArticleElement | ViewItem | ArticleItem>> {
        const availableFormats = this.formats;

        const state: ShareContentOptions = {
            requiresArticleUpdate: true,
            requiresBasketUpdate: true,
        };

        for (const format of formats) {
            if (!availableFormats.includes(format)) {
                return [];
            }

            if (this.requiresArticleUpdate.has(format) && state.requiresArticleUpdate) {
                if (!await this._updateArticle(app, await app.eaiwsBaskets.basket.createArticleManager(app.coreApp))) {
                    return [];
                }
                state.requiresArticleUpdate = false;
            }

            if (this.requiresBasketUpdate.has(format) && state.requiresBasketUpdate) {
                if (!await this._updateBasket(app)) {
                    throw new TaskCanceledError();
                }
                state.requiresBasketUpdate = false;
            }
        }

        return app.basketManager.getVisibleItems();
    }

    private getShareTask(options: ShareOptions): ShareTask {
        return new ShareTask(options, this.callbacks);
    }

    private updateRequirements(
        formats: Array<string>,
        options: ShareContentOptions = {}
    ): void {
        for (const format of formats) {
            if (options.requiresArticleUpdate) {
                this.requiresArticleUpdate.add(format);
            }
            if (options.requiresBasketUpdate) {
                this.requiresBasketUpdate.add(format);
            }
        }
    }

    private _updateArticle: AsyncFunction<boolean, [AppRequirement, ArticleManager]> = updateArticle;
    private _updateBasket: AsyncFunction<boolean, [AppRequirement]> = updateBasket;
};